import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/site/Reveal";

const services = [
  {
    n: "01",
    title: "Architect-led Builds",
    body:
      "We partner with architects to build their work with the fidelity the design deserves. We read drawings closely, ask the right questions early, and stay committed to the details all the way through.",
    for: "For architectural practices",
  },
  {
    n: "02",
    title: "Direct Client Builds",
    body:
      "For owners who want a builder in the room from day one. We help assemble the right team and stay across every detail, so you don't have to.",
    for: "For private clients",
  },
  {
    n: "03",
    title: "Heritage & Additions",
    body:
      "Meticulous restoration combined with quiet contemporary additions. Old and new, working in balance.",
    for: "For heritage-listed sites",
  },
  {
    n: "04",
    title: "Developer Partnerships",
    body:
      "Long-term partnerships with boutique developers who care about finish. Consistent quality, project after project.",
    for: "For boutique developers",
  },
];

const capabilities = [
  "Site & consultant coordination",
  "Heritage-sensitive detailing",
  "Structural steel & engineering liaison",
  "Custom cabinetry & detailing",
  "Material & finish selection",
  "Trade sequencing & scheduling",
  "Final inspections & sign-off",
  "Client walkthroughs at every stage",
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="bg-zinc-950">
      <section className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
              — Services
            </div>
            <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-zinc-100 leading-[1.02]">
              What we build.
            </h1>
            <p className="mt-8 text-zinc-400 text-lg max-w-2xl leading-relaxed">
              Custom homes, extensions and renovations, built with the same
              care regardless of scale. For us, it&apos;s about building a
              home, not just a house — a home shaped around your lifestyle
              that shares meaning and presence. We hold ourselves to a high
              standard on quality, and we bring the same care to the
              relationship — easy to reach, and clear communication from
              start to finish.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900">
          {services.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="bg-zinc-950 p-10 md:p-14 h-full flex flex-col">
                <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                  {s.n} — {s.for}
                </div>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-zinc-100 tracking-tight">
                  {s.title}
                </h2>
                <p className="mt-6 text-zinc-400 leading-relaxed">{s.body}</p>
                <Link
                  to="/contact"
                  data-testid={`services-cta-${s.n}`}
                  className="mt-auto pt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.25em] uppercase text-zinc-100 hover:text-white"
                >
                  Discuss a project
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 sm:py-32 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                — Capabilities
              </div>
              <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl tracking-tighter text-zinc-100 leading-[1.05]">
                One partner, the entire arc of a build.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-10">
                {capabilities.map((c) => (
                  <li
                    key={c}
                    className="flex items-start gap-4 border-b border-zinc-900 pb-4"
                  >
                    <span className="mt-2 w-1.5 h-1.5 bg-zinc-100 shrink-0" />
                    <span className="text-zinc-200 font-serif text-lg">
                      {c}
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
